import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { timetableService } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

const TeacherTimetable: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [timetableEntries, setTimetableEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = [
    { label: '08:00', value: '08:00:00' },
    { label: '09:00', value: '09:00:00' },
    { label: '10:00', value: '10:00:00' },
    { label: '11:00', value: '11:00:00' },
    { label: '12:00', value: '12:00:00' },
    { label: '13:00', value: '13:00:00' }
  ];

  useEffect(() => {
    fetchTimetable();
  }, []);

  const fetchTimetable = async () => {
    setLoading(true);
    try {
      // Fetch all timetable entries for this teacher
      const response = await timetableService.getTeacherTimetable();
      const data = response.data.timetable || [];
      setTimetableEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching timetable:', error);
      setTimetableEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const getEntryForSlot = (day: string, timeSlot: string) => {
    return timetableEntries.find(
      (e: any) => e.dayOfWeek === day && e.startTime === timeSlot
    );
  };

  return (
    <Layout title="My Timetable">
      <div className="space-y-6">
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            My Weekly Schedule
          </h2>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            View your complete teaching schedule for the week
          </p>
        </div>

        {/* Timetable Display */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          {loading ? (
            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Loading timetable...
            </div>
          ) : timetableEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Day / Time
                    </th>
                    {timeSlots.map((slot) => (
                      <th key={slot.value} className={`px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        {slot.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {daysOfWeek.map((day) => (
                    <tr key={day}>
                      <td className={`px-6 py-4 whitespace-nowrap font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {day}
                      </td>
                      {timeSlots.map((slot) => {
                        const entry = getEntryForSlot(day, slot.value);
                        
                        return (
                          <td key={slot.value} className="px-6 py-4 text-center text-sm">
                            {entry ? (
                              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                                <div className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {entry.subject?.name || 'N/A'}
                                </div>
                                <div className={`text-xs mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                  {entry.class?.name} {entry.class?.section}
                                </div>
                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {entry.startTime?.substring(0, 5)} - {entry.endTime?.substring(0, 5)}
                                </div>
                                {entry.room && (
                                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Room: {entry.room}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>Free</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No timetable schedule found
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                Contact the administrator to set up your teaching schedule
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        {timetableEntries.length > 0 && (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Classes
                </p>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {timetableEntries.length}
                </p>
              </div>
              <div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Unique Classes
                </p>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {new Set(timetableEntries.map(e => e.classId)).size}
                </p>
              </div>
              <div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Subjects
                </p>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {new Set(timetableEntries.map(e => e.subject?.name)).size}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherTimetable;
